import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { slice, isEqual, get, noop, first, flowRight as compose } from "lodash";
import { graphql } from "@apollo/client/react/hoc";
import { ApolloClient } from "@apollo/client";
import mediator from "@tvg/mediator";
import {
  getFilteredRaces,
  formatWroBets
} from "@tvg/utils/pastPerformanceUtils";
import getRaceAtiveRunnerBI from "@tvg/utils/getRaceAtiveRunner";
import parseCAPIMessage from "@tvg/utils/capiUtils";
import { doesSilkLoads } from "@tvg/utils/handicappingUtils";
import type {
  PastPerformanceRace,
  RaceBet,
  RaceCardBettingInterest,
  RaceCardRunner
} from "@tvg/ts-types/Race";
import wro from "@tvg/api/wro";
import { formatPastRaceDate } from "@tvg/formatter/dates";
import InlinePastPerformance from "@tvg/atomic-ui/_templates/InlinePastPerformance";
import ModalV2 from "@tvg/atomic-ui/_templates/ModalV2";
import RunnerNumberWithName from "@tvg/atomic-ui/_molecule/RunnerNumberWithName";
import type { ScrollableRef, ContentElement } from "@tvg/ts-types/Modal";
import { isAccountCompliantSelector } from "@tvg/sh-utils/sessionUtils";
import { getAccountNumber } from "@urp/store-selectors";
import { Dispatch } from "redux";
import { ToastManager } from "@tvg/design-system";
import { Device } from "@tvg/ts-types/Device";
import RunnersPastPerformance from "./RunnersPastPerformance";
import PastPerformance from "./PastPerformance";
import InlinePastPerformanceQuery from "./graphql/queries/inlinePastPerformance";
import RunnerStatsQuery from "./graphql/queries/inlinePastPerformanceRunnerStats";
import WagerGroupQuery from "./graphql/queries/wagerGroupQuery";
import {
  ApolloOptionsIPP,
  ApolloOptionsRunnerStats,
  ApolloOptionsWagerPP
} from "./graphql/options.graph";

import { type Props } from "./types";

type State = {
  pastPerformanceRace: PastPerformanceRace | null | undefined;
  pastPerformanceRaceIndex: number;
  hasPreviousRace: boolean;
  hasNextRace: boolean;
  raceListView: boolean;
  isRaceFromRaceListView: boolean;
  isRaceListMatched: boolean;
  runner: RaceCardRunner | null | undefined;
  selectionFromList: boolean;
  raceBets: RaceBet[] | null | undefined;
  activeRunnerBI: RaceCardBettingInterest | null | undefined;
  isLoading: boolean;
  horseSwitcherRunnerName: string;
  horseSwitcherRunnerDob: number;
  horseSwitcherEntityRunnerId: string;
  horseSwitcherRunnerRaces: PastPerformanceRace[];
  silkLoads: boolean;
  isModalOpenAnimationEnded: boolean;
};

const fireUpdateEvent = () => {
  if (typeof window !== "undefined") {
    const event = new CustomEvent("updateResizableArea", {});
    setTimeout(() => {
      window.dispatchEvent(event);
    }, 250);
  }
};

const IPP_RACES_SHOWN_MAX = 4;

export class InlinePastPerformanceComponent extends Component<Props, State> {
  static defaultProps = {
    races: [],
    raceIds: [],
    runnerName: "",
    runnerDob: 0,
    entityRunnerId: "",
    originRaceCode: "",
    originRaceNumber: "",
    originRaceConditions: {
      distance: null,
      surface: null
    },
    isLoading: true,
    isLoadingStats: true,
    ippEmptyStateMsg: "",
    fcpClient: noop,
    behgClient: noop,
    wagerProfile: "PORT-Generic",
    device: "mobile",
    isLogged: false,
    onModalOpen: noop,
    onModalClose: noop,
    hasNavigation: false,
    onTabClick: noop,
    accountNumber: "",
    runners: [],
    raceTypeCode: "T",
    refetch: noop,
    currentRace: {},
    runnerStats: [],
    seeAllPpsMessage: "See All PP's",
    noRunnerCommentMessage: "",
    refetchStats: noop,
    isSilkShown: true,
    handicappingRunnerFlags: {
      title: "",
      subtitle: "",
      subtitleRunnerFlags: "",
      flags: []
    },
    behgToggle: false,
    myBetsResultedBetTicket: false,
    queryGroupWagers: [],
    isTvg5: false,
    selectedRacePP: -1,
    isAccountCompliant: false,
    onClickVideo: noop
  };

  races: PastPerformanceRace[] = [];

  filteredRaces: PastPerformanceRace[] = [];

  filteredRacesIds: string[] = [];

  modalScrollableRef: ScrollableRef;

  constructor(props: Props) {
    super(props);
    this.modalScrollableRef = { current: null };

    this.state = {
      pastPerformanceRace: null,
      pastPerformanceRaceIndex: -1,
      hasPreviousRace: false,
      hasNextRace: false,
      raceListView: false,
      isRaceFromRaceListView: false,
      isRaceListMatched: false,
      runner: null,
      selectionFromList: false,
      raceBets: null,
      activeRunnerBI: null,
      isLoading: true,
      horseSwitcherRunnerName: "",
      horseSwitcherRunnerDob: 0,
      horseSwitcherEntityRunnerId: "",
      horseSwitcherRunnerRaces: [],
      silkLoads: true,
      isModalOpenAnimationEnded: false
    };

    this.races = this.props.races;

    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:UPDATE_RACE_CARD",
      payload: {
        delayed: true
      }
    });
  }

  componentDidMount() {
    fireUpdateEvent();
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    const { races, isLoading, isLoadingStats, isLogged, runnerStats } =
      this.props;

    if (nextProps.isLoading) {
      return false;
    }

    return (
      races.length !== nextProps.races.length ||
      isLoading !== nextProps.isLoading ||
      isLoadingStats !== nextProps.isLoadingStats ||
      isLogged !== nextProps.isLogged ||
      nextState.selectionFromList !== this.state.selectionFromList ||
      nextState.isRaceListMatched !== this.state.isRaceListMatched ||
      nextState.isModalOpenAnimationEnded !==
        this.state.isModalOpenAnimationEnded ||
      !isEqual(runnerStats, nextProps.runnerStats) ||
      !isEqual(this.state, nextState) ||
      !isEqual(races, nextProps.races) ||
      !isEqual(this.props.currentRace, nextProps.currentRace) ||
      this.props.runnerName !== nextProps.runnerName
    );
  }

  static getDerivedStateFromProps(props: Props) {
    return {
      activeRunnerBI: getRaceAtiveRunnerBI(props.runners, props.runnerName)
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.isLoading !== prevProps.isLoading) {
      this.races = this.props.races;

      const filteredResults = getFilteredRaces(
        this.props.races,
        this.props.originRaceConditions
      );
      this.filteredRaces = filteredResults.races;
      this.filteredRacesIds = filteredResults.ids;
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        isLoading: this.props.isLoading
      });

      if (this.props.isTvg5) {
        this.setRace(
          this.props.selectedRacePP > -1 ? this.props.selectedRacePP : 0,
          !(
            this.props.device === Device.MOBILE &&
            this.props.selectedRacePP >= 0
          )
        );
      }

      doesSilkLoads(this.props.runners).then((silkLoads: boolean) => {
        if (this.state.silkLoads !== silkLoads) {
          // eslint-disable-next-line react/no-did-update-set-state
          this.setState({
            silkLoads
          });
        }
      });
    }

    fireUpdateEvent();

    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:UPDATE_RACE_CARD",
      payload: {
        delayed: true
      }
    });
  }

  componentWillUnmount() {
    fireUpdateEvent();

    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:UPDATE_RACE_CARD",
      payload: {
        delayed: false
      }
    });
    // enable scrolling when the modal is automatically dismissed (e.g. when mtp is reached)
    if (
      this.props.currentRace.mtp === 0 &&
      document.body &&
      document.body.style
    ) {
      document.body.style.overflow = "auto";
    }
  }

  setModalScrollableRef = (element: ContentElement) => {
    this.modalScrollableRef = { current: element };
  };

  onMatchConditions = () => {
    const isRaceListMatched = !this.state.isRaceListMatched;
    this.sendMatchingConditionsGTMEvent(
      isRaceListMatched,
      `${get(this.props.originRaceConditions, "distance")} & ${get(
        this.props.originRaceConditions,
        "surface"
      )}`
    );
    const newState = { ...this.state, isRaceListMatched };

    this.setState(newState);
  };

  onMoreRaces = () => {
    const more = () => {
      if (this.props.device !== Device.MOBILE) {
        this.setRace(0, true);
      } else {
        this.setState({
          raceListView: true,
          isRaceFromRaceListView: false
        });
      }
    };

    if (!this.props.isLogged) {
      this.performActionWithLogin(more, "past_performance_click_more_page");
    } else {
      more();
    }
  };

  onOpenMoreRaces = () => {
    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:CLICK_MORE_PAGE"
    });
    this.onMoreRaces();
  };

  onRace = (raceIndex: number) => {
    if (!this.props.isLogged) {
      this.performActionWithLogin(
        this.setRace.bind(this, raceIndex),
        "past_performance_click_on_race"
      );
    } else {
      this.setRace(raceIndex);
    }
  };

  onRaceFromList = (
    raceIndex: number,
    races: PastPerformanceRace[] = this.races
  ) => {
    this.setState(
      {
        raceListView: false,
        selectionFromList: true,
        isRaceFromRaceListView: true
      },
      () => {
        this.setRace(raceIndex, false, races);
      }
    );
  };

  onRaceClose = () => {
    if (this.state.raceListView) {
      this.sendCloseAllPPGTMEvent();
    } else {
      this.sendClosePPGTMEvent();
    }

    this.races = this.props.races;

    this.setState({
      pastPerformanceRace: null,
      pastPerformanceRaceIndex: -1,
      hasPreviousRace: false,
      hasNextRace: false,
      raceListView: false,
      isRaceFromRaceListView: false,
      isRaceListMatched: false,
      runner: null,
      selectionFromList: false,
      raceBets: null,
      horseSwitcherRunnerName: "",
      horseSwitcherRunnerDob: 0,
      horseSwitcherEntityRunnerId: "",
      horseSwitcherRunnerRaces: []
    });

    ToastManager.hide();
  };

  onBackModal = () => {
    const { runner, pastPerformanceRace } = this.state;

    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:BACK_TO_ALL",
      payload: {
        trackName: get(pastPerformanceRace, "track.name", ""),
        runnerName: get(runner, "horseName", ""),
        raceNumber: get(pastPerformanceRace, "number", "")
      }
    });
    this.onMoreRaces();
  };

  onRunnerClick = (
    horseSwitcherRunnerName: string,
    horseSwitcherRunnerDob: number,
    horseSwitcherEntityRunnerId: string,
    index: number
  ) => {
    // scroll to top of modal container
    if (this.modalScrollableRef.current)
      this.modalScrollableRef.current.scrollTop = 0;

    this.sendHorseSelectorGTM(index);
    this.setState({
      horseSwitcherRunnerName,
      horseSwitcherRunnerDob,
      horseSwitcherEntityRunnerId,
      isRaceListMatched: false,
      horseSwitcherRunnerRaces: []
    });
  };

  setPreviousRace = () => {
    const { pastPerformanceRaceIndex, hasPreviousRace } = this.state;

    if (hasPreviousRace) {
      this.sendNavigationGTMEvent("Previous");
      this.setRace(
        pastPerformanceRaceIndex - 1,
        false,
        this.state.horseSwitcherRunnerRaces
      );
    }
  };

  setNextRace = () => {
    const { pastPerformanceRaceIndex, hasNextRace } = this.state;

    if (hasNextRace) {
      this.sendNavigationGTMEvent("Next");
      this.setRace(
        pastPerformanceRaceIndex + 1,
        false,
        this.state.horseSwitcherRunnerRaces
      );
    }
  };

  setRace = (
    raceIndex: number,
    raceListView: boolean = false,
    horseSwitcherRunnerRaces: PastPerformanceRace[] = []
  ) => {
    const newRaces =
      horseSwitcherRunnerRaces.length > 0
        ? horseSwitcherRunnerRaces
        : this.races;
    const pastPerformanceRace = get(newRaces, `[${raceIndex}]`, null);
    const pastPerformanceRaceIndex = pastPerformanceRace ? raceIndex : -1;
    const hasPreviousRace = pastPerformanceRaceIndex > 0;
    const hasNextRace = pastPerformanceRaceIndex < newRaces.length - 1;

    this.props.onModalOpen();
    this.setState(
      {
        pastPerformanceRace,
        pastPerformanceRaceIndex,
        hasPreviousRace,
        hasNextRace,
        raceListView,
        horseSwitcherRunnerRaces
      },
      this.fetchBets
    );
  };

  getRaceBets = () => {
    const date = formatPastRaceDate(
      get(this.state, "pastPerformanceRace.date", "")
    );

    return wro
      .getMyBets(
        {
          startDate: date,
          endDate: date,
          track: get(this.state, "pastPerformanceRace.track.code"),
          race: +get(this.state, "pastPerformanceRace.number")
        },
        this.props.accountNumber
      )
      .then((res) => (res.status === 200 ? formatWroBets(res) : []))
      .catch(() => []);
  };

  getRacesSubset = (length: number) => {
    const { races } = this.props;

    if (length > IPP_RACES_SHOWN_MAX) {
      return slice(races, 0, IPP_RACES_SHOWN_MAX);
    }

    return races;
  };

  fetchBets = () => {
    if (this.state.pastPerformanceRace && !this.props.behgToggle) {
      this.getRaceBets().then((res) => this.setState({ raceBets: res }));
    }
  };

  performActionWithLogin = (cb: () => void, triggerAction: string) => {
    mediator.base.dispatch({
      type: "OPEN_LOGIN",
      payload: {
        callback: (_error: unknown, success: unknown) => {
          /* istanbul ignore if */
          if (get(success, "status", "fail") === "success") {
            cb();
          }
        },
        triggerAction
      }
    });
  };

  sendClickGTMEvent = (runner: RaceCardRunner) => {
    if (this.state.selectionFromList) {
      mediator.base.dispatch({
        type: "PAST_PERFORMANCES:SELECT_PP_MORE_PAGE",
        payload: {
          trackName: get(this.state.pastPerformanceRace, "track.name", ""),
          runnerName: runner.horseName
        }
      });
    } else {
      mediator.base.dispatch({
        type: "PAST_PERFORMANCES:CLICK_PP",
        payload: {
          trackName: get(this.state.pastPerformanceRace, "track.name", ""),
          runnerName: runner.horseName,
          raceNumber: get(this.state.pastPerformanceRace, "number", "")
        }
      });
    }
  };

  sendNavigationGTMEvent = (direction: string) => {
    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:NAVIGATE_PP",
      payload: {
        direction,
        trackName: get(this.state.pastPerformanceRace, "track.name", ""),
        runnerName: get(this.state.runner, "horseName", ""),
        raceNumber: get(this.state.pastPerformanceRace, "number", "")
      }
    });
  };

  sendCloseAllPPGTMEvent = () => {
    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:CLOSE_ALL_PP"
    });
  };

  sendClosePPGTMEvent = () => {
    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:CLOSE_SINGLE_PP"
    });
  };

  sendMatchingConditionsGTMEvent = (
    isToggleEnabled: boolean,
    matchingConditions: string
  ) => {
    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:TOGGLE_CONDITIONS",
      payload: {
        toggleState: isToggleEnabled ? "On" : "Off",
        matchingConditions
      }
    });
  };

  sendHorseSelectorGTM = (index: number) => {
    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:HORSE_SELECTOR",
      payload: { runnerIndex: index + 1 } // index starts at 0
    });
  };

  updateRunner = (runner: RaceCardRunner) => {
    this.setState({ runner });
  };

  renderPastPerformance = (
    pastPerformanceRace: PastPerformanceRace | null | undefined,
    pastPerformanceRaceIndex: number,
    originRace: string
  ) =>
    pastPerformanceRace ? (
      <PastPerformance
        // @ts-ignore
        device={this.state.device}
        races={
          this.state.raceListView || this.props.device !== Device.MOBILE
            ? this.races
            : []
        }
        selectedRace={pastPerformanceRace}
        selectedRaceIndex={pastPerformanceRaceIndex}
        runnerName={
          this.state.horseSwitcherRunnerName
            ? this.state.horseSwitcherRunnerName
            : this.props.runnerName
        }
        trackCode={get(pastPerformanceRace, "track.code", "")}
        raceNumber={get(pastPerformanceRace, "number", "")}
        raceDate={formatPastRaceDate(get(pastPerformanceRace, "raceDate", ""))}
        wagerProfile={this.props.wagerProfile}
        fcpClient={this.props.fcpClient as ApolloClient<unknown>}
        hasPreviousRace={this.state.hasPreviousRace}
        hasNextRace={this.state.hasNextRace}
        onPreviousRaceNavigation={this.setPreviousRace}
        onNextRaceNavigation={this.setNextRace}
        onRaceFromList={
          this.props.device !== Device.MOBILE ? this.onRaceFromList : noop
        }
        isRaceListView={this.state.raceListView}
        onClickGTMEvent={this.sendClickGTMEvent}
        updateRunner={this.updateRunner}
        onMatchConditions={this.onMatchConditions}
        isMatchConditionsOn={this.state.isRaceListMatched}
        raceBets={this.state.raceBets}
        originRace={originRace}
        raceTypeCode={this.props.raceTypeCode}
        activeRunner={this.props.runnerName}
        activeRunnerBI={this.state.activeRunnerBI}
        isLoadingRunner={this.state.isLoading}
        handicappingRunnerFlags={this.props.handicappingRunnerFlags}
        currentRace={this.props.currentRace}
        onModalClose={this.onRaceClose}
        wagerGroup={first(this.props.queryGroupWagers)}
        isVideoIframe={this.props.isTvg5}
        isTvg5={this.props.isTvg5}
        isAutoPlay={this.props.isTvg5 && this.props.selectedRacePP >= 0}
      />
    ) : null;

  renderRunnersPastPerformance = (
    pastPerformanceRaceIndex: number = -1,
    children: React.ReactNode | null = null
  ) => (
    // @ts-ignore
    <RunnersPastPerformance
      runnerName={
        this.state.horseSwitcherRunnerName
          ? this.state.horseSwitcherRunnerName
          : this.props.runnerName
      }
      runnerDob={
        this.state.horseSwitcherRunnerDob
          ? this.state.horseSwitcherRunnerDob
          : this.props.runnerDob
      }
      entityRunnerId={
        this.state.horseSwitcherEntityRunnerId
          ? this.state.horseSwitcherEntityRunnerId
          : this.props.entityRunnerId
      }
      currentRace={this.props.currentRace}
      originRaceConditions={this.props.originRaceConditions}
      isMatchConditionsOn={this.state.isRaceListMatched}
      wagerProfile={this.props.wagerProfile}
      fcpClient={this.props.fcpClient}
      device={this.props.device}
      runners={this.props.runners}
      raceTypeCode={this.props.raceTypeCode}
      onRunnerClick={this.onRunnerClick}
      onRaceFromList={this.onRaceFromList}
      onMatchConditions={this.onMatchConditions}
      onModalClose={this.props.onModalClose}
      isSilkShown={this.state.silkLoads}
      borderless={!children}
      pastPerformanceRaceIndex={pastPerformanceRaceIndex}
      isHorseSwitcherDisabled={!this.state.isModalOpenAnimationEnded}
      onClickVideo={this.props.onClickVideo}
    >
      {children}
    </RunnersPastPerformance>
  );

  renderModal = () => {
    const {
      pastPerformanceRace,
      pastPerformanceRaceIndex,
      raceListView,
      isRaceFromRaceListView
    } = this.state;
    const isOpen = raceListView || !!pastPerformanceRace;
    const onBack =
      this.props.hasNavigation && isRaceFromRaceListView
        ? this.onBackModal
        : null;

    const modalProps = {
      ...{
        isLastModal: true,
        isOpen,
        onClose: () => {
          this.onRaceClose();
          if (
            this.props.isTvg5 &&
            this.props.onModalClose &&
            typeof this.props.onModalClose === "function"
          ) {
            this.props.onModalClose();
          }
          this.setState({ isModalOpenAnimationEnded: false });
        },
        onOpenAnimationEnd: () => {
          this.setState({ isModalOpenAnimationEnded: true });
        }
      },
      ...(this.props.device === Device.MOBILE
        ? {
            title: "Race Replay",
            titleType: "default",
            subtitle: this.props.runnerName,
            animation: "bottom",
            onBack
          }
        : {
            title: "Race Replay",
            titleType: "default",
            subtitle: this.props.runnerName,
            animation: "fade",
            offsetTop: 40,
            offsetLeft: 40,
            offsetRight: 40,
            offsetBottom: 40
          })
    };

    let modalContent: React.ReactNode | null = null;
    const originRace = `${this.props.originRaceCode} - R${this.props.originRaceNumber}`;

    if (isOpen) {
      modalProps.title = "Horse info and PP's";
      modalProps.titleType = "ipp";
      // @ts-ignore
      modalProps.subtitle = null;

      if (this.props.device === Device.MOBILE) {
        if (raceListView) {
          modalContent = this.renderRunnersPastPerformance();
        } else {
          // @ts-ignore
          modalProps.title = (
            <>
              <RunnerNumberWithName
                raceTypeCode={get(pastPerformanceRace, "raceTypeCode", "T")}
                runnerId={get(pastPerformanceRace, "runnerNumber", "")}
                runnerName={
                  this.state.horseSwitcherRunnerName
                    ? this.state.horseSwitcherRunnerName
                    : this.props.runnerName
                }
              />
            </>
          );

          modalContent = this.renderPastPerformance(
            pastPerformanceRace,
            pastPerformanceRaceIndex,
            originRace
          );
        }
      } else {
        modalContent = this.renderRunnersPastPerformance(
          pastPerformanceRaceIndex,
          this.renderPastPerformance(
            pastPerformanceRace,
            pastPerformanceRaceIndex,
            originRace
          )
        );
      }
    }

    return (
      <ModalV2
        {...modalProps}
        qaLabel="inline-past-performances-modal"
        scrollableRef={this.modalScrollableRef}
        setScrollableRef={this.setModalScrollableRef}
      >
        {/* istanbul ignore next */ () => modalContent}
      </ModalV2>
    );
  };

  render() {
    const totalRaces = this.props.races.length;
    const races = this.getRacesSubset(totalRaces);
    const totalMoreRaces = totalRaces - races.length;
    return this.props.isTvg5 ? (
      this.renderModal()
    ) : (
      <>
        <InlinePastPerformance
          races={races}
          totalRaces={totalMoreRaces}
          isLoading={this.props.isLoading}
          emptyMessage={this.props.ippEmptyStateMsg}
          onRace={this.onRace}
          onMoreRaces={this.onOpenMoreRaces}
          onRaceCard={this.props.hasNavigation && totalRaces > 0}
          raceTypeCode={this.props.raceTypeCode}
          activeRunner={this.props.runnerName}
          activeRunnerBI={this.state.activeRunnerBI}
          runnerStats={this.props.runnerStats}
          seeAllPpsMessage={this.props.seeAllPpsMessage}
          noRunnerCommentMessage={this.props.noRunnerCommentMessage}
          isSilkShown={this.props.isSilkShown && this.state.silkLoads}
          isLoadingStats={this.props.isLoadingStats}
          handicappingRunnerFlags={this.props.handicappingRunnerFlags}
        />
        {this.renderModal()}
      </>
    );
  }
}

export default connect(
  /* istanbul ignore next */
  (store) => ({
    isLogged: get(store, "userData.logged"),
    ippEmptyStateMsg: get(store, "capi.messages.ippEmptyStateMsg", false),
    seeAllPpsMessage: get(
      store,
      "capi.messages.seeAllPpsMessage",
      "~runnerName~'s Info and PPs"
    ),
    // @ts-ignore
    accountNumber: getAccountNumber(store),
    hasNavigation: get(
      store,
      "capi.featureToggles.inLinePastPerformanceNavigation",
      false
    ),
    noRunnerCommentMessage: get(
      store,
      "capi.messages.noRunnerCommentMessage",
      ""
    ),
    handicappingRunnerFlags: parseCAPIMessage(
      store,
      "capi.messages.handicappingRunnerFlags"
    ),
    behgToggle: get(store, "capi.featureToggles.tvgenablebehg", false),
    myBetsResultedBetTicket: get(
      store,
      "capi.featureToggles.myBetsResultedBetTicket",
      false
    ),
    // @ts-ignore
    isAccountCompliant: isAccountCompliantSelector(store)
  }),
  /* istanbul ignore next */
  (dispatch: Dispatch) => ({ dispatch })
)(
  compose(
    // @ts-ignore
    graphql(InlinePastPerformanceQuery, ApolloOptionsIPP),
    // @ts-ignore
    graphql(RunnerStatsQuery, ApolloOptionsRunnerStats),
    // @ts-ignore
    graphql(WagerGroupQuery, ApolloOptionsWagerPP)
  )(InlinePastPerformanceComponent)
);
